import { useMemo, useCallback } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import _ from 'lodash'
import classNames from 'classnames'
import { isInManageRoute, isValidRoute, showLoadingMask } from './fawkesUtils'
import { generateRoutes } from './navUtils'
import { globalAvail, tsgAccessAvail } from '../common/utils'
import { logout } from '../../framework/actions'
import { PathNotAvailable, NoPathAvailable, FawkesNotAvailable, NoTenantAccess } from '../common/PathNotAvailable'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { hasMicroApp } from '../../framework/microApps'
import { hasTsgSupport } from '../../framework/tsg'
import {
  MGMT_OVERVIEW_ROUTE,
  PA_DEFAULT_ROUTE,
  FAWKES_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  CIE_DEFAULT_ROUTE,
  CIE_APP_ID,
  FAWKES_APP_ID,
  CN_REGION,
  DASHBOARDS_MICRO_APP,
  PAI_MICRO_APP_NAME,
  DASHBOARDS_PATH_PREFIX,
} from '../../framework/constants'
import { PANWDS_TW2_CLS } from '../../patches/panwds-constants'
import { getFrameworkVar } from '../../framework/states'
import { getFawkesDefaultRedirect } from './handleLogin'
import { __info, __isDebugOn, __isLocalhost } from '../../init/debug'
import type { VFC } from 'react'
import type { NavItem, NavAction, ComponentWrapperProps } from '../../framework/microAppTypes'
import type { EventEmitter } from '../../framework/events'
import type {
  AnyState,
  FeatureInfo,
  StoreState,
  StateRef,
  Dispatch,
  LoggedInUser,
  UserState,
  Vars,
} from '../../framework/stateTypes'
import { RedirectionViews } from '../../framework/enums'

const logoutRoute = () => {
  logout()
  return null
}

interface RouteViewerProps {
  leftNavComponent: any
  loggedInUser: LoggedInUser | (UserState & { subname: string; username: string; }) | undefined
  tenant: AnyState | undefined
  navigation: (NavItem | NavAction)[] | undefined
  leftMainRoutes: NavItem[] | undefined
  licenseInfo: AnyState | undefined
  featuresInfo: FeatureInfo[] | undefined
  hideSideMenu: () => void
  _showSideMenu: boolean
  state: StoreState
  getState: () => StoreState
  stateRef: StateRef
  dispatch: Dispatch
  events: EventEmitter
  //routeContext: AnyState
  RouteWrapper: VFC<ComponentWrapperProps>
  tokenInfo: any
  defaultRoute: string | undefined
  vars: Vars
}

//type NavRoutes = ReactElement[] // & { defaultNav?: NavItem }

const NO_REDIRECT = 'no-redirect'

let lastLoggedItem: NavItem | undefined
const __logNavItem = (navItem: NavItem, log: string) => {
  if (__isDebugOn() && lastLoggedItem !== navItem) {
    __info('[route]', log, navItem.path, navItem)
    lastLoggedItem = navItem
  }
}

const RouteViewer: VFC<RouteViewerProps> = (props) => {
  const {
    navigation,
    leftMainRoutes,
    defaultRoute,
    leftNavComponent,
    RouteWrapper,
    state,
    stateRef,
  } = props
  const isGlobalAvail = globalAvail()
  const baseRoute = defaultRoute && defaultRoute !== '/' ? defaultRoute : undefined

  const navigationGlobalAvail = useMemo(() => {
    return isGlobalAvail ? navigation : navigation?.filter(navItem => navItem.bypassGlobalAvail)
  }, [navigation, isGlobalAvail])

  const isValidRouteFn = useCallback((nav: NavItem): boolean | 'loading' | 'no-redirect' => {
    if (nav.isLoading && nav.isLoading()) {
      return 'loading' // is loading
    }
    if (nav.availFallbackRedirect === NO_REDIRECT) {
      return NO_REDIRECT
    }
    // below as a copy in fawkesUtils, ensure in sync
    return isValidRoute(nav)
  }, [])

  const renderRouteComponent = useCallback((navItem: NavItem, parent?: NavItem | null) => {
    if (!navItem.path) {
      return
    }
    if (navItem.redirectTo) {
      if (navItem.component && navItem.exact) {
        console.warn('redirectTo is not allowed if the nav item has component with exact: true')
      }
      else {
        return (<Route
          key={`${navItem.path}-redirect`}
          path={navItem.path}
          exact={true}
          render={() => {
            const to = _.isFunction(navItem.redirectTo) ? navItem.redirectTo() : navItem.redirectTo!
            __logNavItem(navItem, `redirectTo path ${to}`)
            return <Redirect to={to} />
          }}
        />)
      }
    }
    // all the leaf nodes with component will create a route
    return (
      <Route
        key={navItem.path}
        exact={navItem.exact}
        path={navItem.path}
        render={(renderProps) => {
          const isAvail = isValidRouteFn(navItem)
          if (isAvail === 'loading') {
            return <LoadingPlaceholder />
          }
          if (isAvail) {
            __logNavItem(navItem, `render component ${navItem.exact ? 'exact' : ''}`)
            const cls = classNames(
              'oneapp-body-container',
              'spf-w-full spf-min-h-full spf-p-0',
              !(navItem.contentClassName && /\bpanwds-(?:tw3|mixed)\b/.test(navItem.contentClassName)) && PANWDS_TW2_CLS, // tw2 by default now
              navItem.contentClassName,
            )
            return (
              <div className={cls}>
                <RouteWrapper
                  navItem={navItem}
                  hasParent={Boolean(parent)}
                  renderProps={renderProps}
                  parentProps={props}
                />
              </div>
            )
          }
          if (navItem.availFallbackRedirect) {
            const redirect = _.isFunction(navItem.availFallbackRedirect) ? navItem.availFallbackRedirect() :
              _.isString(navItem.availFallbackRedirect) ? navItem.availFallbackRedirect : undefined
            if (redirect?.startsWith('/')) {
              __logNavItem(navItem, `fallback redirect to ${redirect}`)
              return <Redirect to={redirect} />
            }
            else if (redirect) {
              console.error('the result of availFallbackRedirect should be a path starts with /')
            }
          }
          const defaultRoute = baseRoute
          const defaultRouteNotAvail = Boolean(defaultRoute &&
            (navItem.path === defaultRoute || (
              defaultRoute === PA_DEFAULT_ROUTE && isInManageRoute(navItem.path!)
            ))
          )
          if (!globalAvail()) {
            __logNavItem(navItem, 'global avail not passed')
            return <NoPathAvailable />
          }
          if (!tsgAccessAvail()) {
            __logNavItem(navItem, 'tsg avail not passed')
            return <NoTenantAccess auth={stateRef.current.auth} isIPRestricted={Boolean(stateRef?.current?.auth?.instances?.error?.ipRestricted)} />
          }
          if (!defaultRouteNotAvail) {
            const to = defaultRoute || '/'
            __logNavItem(navItem, `redirect to ${to} due to not avail`)
            // no permission for this route. redirect to defaultRoute
            return <Redirect to={to} />
          }
          __logNavItem(navItem, 'path not avail and default path not avail')
          return <PathNotAvailable location={renderProps.location} />
        }}
      />
    )
  }, [RouteWrapper, baseRoute, isValidRouteFn, props, stateRef])

  const navRoutes = useMemo(() => generateRoutes(navigationGlobalAvail, leftMainRoutes || []), [leftMainRoutes, navigationGlobalAvail])
  const navigationRoutes = useMemo(() => {
    const routesWithComponent = navRoutes?.map((route: any) => renderRouteComponent(route, route?.parent))
    return routesWithComponent
  }, [navRoutes, renderRouteComponent])

  const redirectFawkes = useCallback(() => {
    if (!globalAvail()) {
      return <NoPathAvailable />
    }
    const errorState = state.main?.fawkesSessionError || {}
    if (errorState.message) {
      return <FawkesNotAvailable {...errorState} />
    }
    const instances = stateRef.current.auth?.instances
    const fawkesInst = instances?.get(FAWKES_APP_ID)
    const isSaseIaView = instances?.flags?.redirectionView === RedirectionViews.SASE_IA && hasMicroApp('sase_ia')
    const isPAWithoutFawkes = fawkesInst && (fawkesInst.platform_region || fawkesInst.region) === CN_REGION && !fawkesInst.runtime_attributes?.admin_api_url
    if (isPAWithoutFawkes && !isSaseIaView && hasMicroApp(PAI_MICRO_APP_NAME)) {
      return <Redirect to='/insights' />
    }
    const fawkesRedirect = getFawkesDefaultRedirect() || MGMT_OVERVIEW_ROUTE
    let microAppNameToCheck: string | undefined
    let firstLevelPath: string | undefined
    if (isInManageRoute(fawkesRedirect)) {
      microAppNameToCheck = FAWKES_MICRO_APP_NAME
      firstLevelPath = MGMT_OVERVIEW_ROUTE
    }
    else if (fawkesRedirect.startsWith(DASHBOARDS_PATH_PREFIX)) {
      microAppNameToCheck = DASHBOARDS_MICRO_APP
      firstLevelPath = DASHBOARDS_PATH_PREFIX
    }
    else if (fawkesRedirect.startsWith('/insights')) {
      microAppNameToCheck = 'pai'
      firstLevelPath = '/insights'
    }
    else if (fawkesRedirect.startsWith('/dem')) {
      microAppNameToCheck = 'dem'
      firstLevelPath = '/dem'
    }
    if (microAppNameToCheck && !hasMicroApp(microAppNameToCheck)) {
      console.error(`micro-app "${microAppNameToCheck}" is not available for ${PA_DEFAULT_ROUTE} redirect to ${fawkesRedirect}`)
      return <FawkesNotAvailable message={`${firstLevelPath || PA_DEFAULT_ROUTE} is not available.`} from={errorState.from} />
    }
    const matchedItem = firstLevelPath && hasTsgSupport() && navigation?.find(item => item.path === firstLevelPath)
    const isMatchedItemValid = matchedItem && isValidRoute(matchedItem)
    if (firstLevelPath === MGMT_OVERVIEW_ROUTE && !isMatchedItemValid) {
      const url = stateRef.current?.auth?.instances?.get(FAWKES_APP_ID)?.url
      if (url && new URL(url).origin !== window.location.origin && !__isLocalhost &&
        hasMicroApp(FAWKES_MICRO_APP_NAME) && getFrameworkVar('tsg_auto_redirection') !== false) {
        console.warn('redirect to url due to current prisma-access not accessible', url)
        showLoadingMask()
        window.location.href = url
        return null
      }
      else if (url) {
        return <FawkesNotAvailable message={`${firstLevelPath} is not available.`} from={errorState.from} />
      }
    }
    if (matchedItem && !isMatchedItemValid &&
      baseRoute?.startsWith(MSP_PATH_PREFIX)) {
      return <FawkesNotAvailable message={`${firstLevelPath} is not available.`} from={errorState.from} />
    }
    return <Redirect to={fawkesRedirect} />
  }, [baseRoute, navigation, stateRef, state.main?.fawkesSessionError])

  const microAppDefaultRoutes = useMemo(() => {
    const routes = []
    const hasPARoute = navigation?.some(nav => nav?.path === PA_DEFAULT_ROUTE)
    if (!hasPARoute) {
      routes.push(<Route key='prisma-access' exact path={PA_DEFAULT_ROUTE} render={redirectFawkes} />)
    }
    if (getFrameworkVar('cie_auto_redirect') !== false) { // on by default
      const hasCIERoute = navigation?.some(nav => nav?.path === CIE_DEFAULT_ROUTE)
      const cieInst = state?.auth?.instances?.get(CIE_APP_ID)
      if (!hasCIERoute && cieInst?.url) {
        routes.push(<Route key='cie' exact path={CIE_DEFAULT_ROUTE} render={() => {
          showLoadingMask()
          window.location.href = cieInst.url!
          return null
        }} />)
      }
    }
    return routes
  }, [navigation, redirectFawkes, state?.auth?.instances])

  return (
    <div data-testid='sparky-oneapp' className='sparky-oneapp spf-flex spf-flex-row spf-h-screen'>
      <div data-testid='sparky-left-nav' className='sparky-left-nav panwds-tw3 panwds-unset-reboot spf-flex-0 spf-z-[1000]'>
        {leftNavComponent}
      </div>
      <div
        id='mainframe'
        data-testid='sparky-main-body'
        className='sparky-main-body spf-w-full spf-h-screen spf-flex spf-flex-grow spf-relative spf-overflow-auto spf-bg-gray-100 dark:spf-bg-gray-950'
      >
        <Switch>
          {navigationRoutes}
          <Route key='logout' path='/logout' render={logoutRoute} />
          <Route key='auth-callback' path='/authentication/callback' render={() => null} />
          {microAppDefaultRoutes}
          {isGlobalAvail ?
            (baseRoute && navRoutes.length ? <Redirect from='/' to={baseRoute} /> : null) :
            <Route key='none' path='/' component={NoPathAvailable} />
          }
        </Switch>
      </div>
    </div>
  )

}

export default RouteViewer
